import { apiService } from '../api/apiService'
import { endpoints } from '../api/endpoints'
import { errorService } from '../error/errorService'
import { createBaseEntityService } from './baseEntityService'

/**
 * Service for handling site-related API operations
 * Updated to work with new backend API structure and smart endpoint selection
 */
const baseService = createBaseEntityService({
  endpoints: endpoints.sites,
  entityName: 'site',
  entityType: 'sites',
  paramKeys: ['customer_id'], // Sites can be filtered by customer_id
})

export const siteService = {
  ...baseService,

  /**
   * Get sites for a specific customer
   * @param {string} customerId - Customer ID
   * @param {object} options - Additional options
   * @returns {Promise<object>} Paginated list of sites for the customer
   */
  async getByCustomer(customerId, options = {}) {
    // Use the main getAll method with customer filter
    return this.getAll({
      customer_id: customerId,
      ...options,
    })
  },

  /**
   * Get aggregated cycle times for all vehicles at a site
   * @param {string} siteId - Site ID
   * @param {object} options - Query options (start_date, end_date, skip, limit, timezone)
   * @returns {Promise<object>} Aggregated cycle times with vehicle_id included
   */
  async getCycleTimes(siteId, options = {}) {
    try {
      const response = await apiService.get(endpoints.sites.getCycleTimes(siteId), {
        params: {
          start_date: options.start_date,
          end_date: options.end_date,
          skip: options.skip || 0,
          limit: options.limit || 1000,
          timezone: options.timezone,
        },
      })
      // Backend returns: {site_id, cycles: [{...with vehicle_id}], total, date_range}
      return response.data || response
    } catch (error) {
      errorService.logError(error, {
        context: 'Site service - getCycleTimes',
        siteId,
        options,
      })
      throw error
    }
  },

  /**
   * Get video timeline for multiple dates in a single batch request
   * @param {string} siteId - Site ID
   * @param {object} options - Query options
   * @param {Array<string>} options.dates - Array of dates (YYYY-MM-DD format, max 100)
   * @param {boolean} options.summary_only - Return only vehicle counts (default: true)
   * @param {number} options.min_vehicle_count - Filter dates with at least N vehicles
   * @returns {Promise<object>} Batch video timeline data
   */
  async getVideoTimelineBatch(siteId, options = {}) {
    try {
      const response = await apiService.post(endpoints.sites.getVideoTimelineBatch(siteId), {
        dates: options.dates || [],
        summary_only: options.summary_only !== false, // default: true
        min_vehicle_count: options.min_vehicle_count,
      })
      // Backend returns: {site_id, dates: [{date, vehicle_count, has_video}], total_dates_checked, dates_with_min_vehicles}
      return response.data || response
    } catch (error) {
      errorService.logError(error, {
        context: 'Site service - getVideoTimelineBatch',
        siteId,
        options,
      })
      throw error
    }
  },
}
